<template>
  <div
    :id="`section-graph-${section.id}`"
    class="flex flex-col w-full blum-report-graph"
    :class="{ 'section-focused': isFocused }"
    :style="{ padding: '0px 0px 24px 0px', gap: '8px' }"
  >
    <div class="flex flex-col items-center text-center" :style="{ gap: '8px' }">
      <!-- <BlTextSubtitle3 :text="collection.title" /> -->
      <div v-if="collection.masteredDate">
        <BlTextBody2 text="Mastered on" :style="{ fontSize: '14px' }" />
        <BlTextBody2
          :text="
            displayDate({
              date: collection.masteredDate,
              format: 'DD MMM YYYY'
            })
          "
          :style="{ fontSize: '14px' }"
        />
      </div>
      <div v-else-if="collection.startDate && collection.endDate">
        <BlTextBody2 text="Progress from" :style="{ fontSize: '14px' }" />
        <BlTextBody2
          :text="displayDate({ date: collection.startDate })"
          :style="{ fontSize: '14px' }"
        />
        <BlTextBody2 text="to" :style="{ fontSize: '14px' }" />
        <BlTextBody2
          :text="displayDate({ date: collection.endDate })"
          :style="{ fontSize: '14px' }"
        />
      </div>
    </div>
    <div
      v-if="collection.labels.length > 20 && useDateInputs && mode === 'edit'"
      class="flex items-center rounded bg-tulip-200"
      :style="{ padding: '8px 16px', gap: '8px' }"
    >
      <BlIcon
        :style="{ height: '24px', width: '24px' }"
        icon="warning"
        color="tulip-7"
      />
      <div class="flex flex-col">
        <BlTextBody2
          text="Your graph contains over 20 data points, which may make it difficult to read. Consider adjusting the date range for better clarity."
          class="text-tulip-800"
        />
      </div>
    </div>
    <div class="flex flex-col w-full" :style="{ gap: '8px' }">
      <!-- Y-axis label -->
      <div
        v-if="section.kind.includes('cumulative')"
        class="text-grey-400"
        :style="{ fontSize: '12px' }"
      >
        Number of mastered targets
      </div>
      <!-- Graph Preview -->
      <div
        v-if="showDelay"
        class="grid w-full rounded h-100 place-items-center bg-slate-100 animate-pulse"
      ></div>
      <div
        v-else-if="graphPreviewLoading"
        class="grid w-full h-100 place-items-center"
      >
        <div class="blum-loader"></div>
      </div>
      <div v-else class="relative flex items-center justify-center flex-grow">
        <BarChart
          v-if="section.kind === 'cumulative_graph_stacked'"
          :key="`cumulative-stacked-chart-${section.id}-${section.updated_at}`"
          class="w-full"
          :options="stackedChartOptions"
          :chart-data="collection"
        />
        <LineChart
          v-else-if="section.kind !== 'graph_sbt'"
          :key="`target-line-chart-${section.id}-${section.updated_at}`"
          class="w-full"
          :options="lineChartOptions"
          :chart-data="collection"
          :plugins="[multiArbitraryLine]"
        />
        <div v-else class="w-full">
          <MixedChart :chart-data="collection" :chart-grid-show="true" />
        </div>
        <div
          v-if="isEmptyGraph"
          class="absolute flex items-center rounded bg-tulip-200"
          :style="{ padding: '8px 16px', gap: '8px' }"
        >
          <BlIcon
            :style="{ height: '24px', width: '24px' }"
            icon="warning"
            color="tulip-7"
          />
          <div class="flex flex-col">
            <BlTextBody2
              text="No progress found within the selected date range."
              class="text-tulip-800"
            />
            <BlTextBody2
              :text="
                `Adjust the date range to display data for this ${
                  section.kind.includes('cumulative') ? 'graph' : 'target'
                }.`
              "
              class="text-tulip-800"
            />
          </div>
        </div>
      </div>
      <!-- X-axis label -->
      <div
        v-if="section.kind.includes('cumulative')"
        class="self-center text-grey-400"
        :style="{ fontSize: '12px' }"
      >
        Date
      </div>
    </div>
    <!-- Prompting Legends -->
    <div
      v-if="
        section.kind === 'graph_prompting' &&
          (promptingProgressBy === 'frequency' ||
            promptingProgressBy === 'percentage')
      "
      class="grid grid-cols-5 rounded-lg bg-slate-200"
      :style="{ padding: '12px 12px', gap: '16px' }"
    >
      <div
        v-for="prompt in filterUniqueLegends(collection.prompts)"
        :key="prompt.id"
        class="flex items-center"
        :style="{ gap: '8px' }"
        :title="prompt.name"
      >
        <BlIcon
          v-if="prompt.shape && prompt.color"
          :style="{ width: '20px' }"
          :icon="'prompt-' + prompt.shape"
          :color="prompt.color"
        />
        <BlTextBody2
          :text="prompt.name"
          class="truncate capitalize-first"
          :style="{ fontSize: '12px' }"
        />
      </div>
      <div
        v-if="collection.goal"
        class="flex items-center"
        :style="{ gap: '8px' }"
      >
        <div
          class="flex-shrink-0 rounded-full"
          :style="{ backgroundColor: '#71C115', height: '4px', width: '48px' }"
        ></div>
        <BlTextBody2 text="Goal" :style="{ fontSize: '12px' }" />
      </div>
    </div>
    <!-- SBT Legends -->
    <div
      v-else-if="section.kind === 'graph_sbt'"
      class="grid grid-cols-5 rounded-lg bg-slate-200"
      :style="{ padding: '12px 12px', gap: '16px' }"
    >
      <div
        v-for="(legend, idx) in filterUniqueLegends(legends)"
        :key="`legend-${idx}`"
        class="flex items-center"
        :style="{ gap: '8px' }"
        :title="legend.label"
      >
        <div
          v-if="legend.type === 'problemBehavior'"
          class="flex-shrink-0 rounded"
          :style="{
            backgroundColor: legend.color,
            height: '16px',
            width: '16px'
          }"
        ></div>
        <div
          v-else
          class="flex-shrink-0"
          :style="{
            backgroundColor: legend.color,
            height: '2px',
            width: '32px'
          }"
        ></div>
        <BlTextBody2
          :text="legend.label"
          class="truncate capitalize-first"
          :style="{ fontSize: '12px' }"
        />
      </div>
    </div>
    <!-- General Legends -->
    <div
      v-else-if="section.kind !== 'cumulative_graph_line'"
      class="grid grid-cols-5 rounded-lg bg-slate-200"
      :style="{ padding: '12px 12px', gap: '16px' }"
    >
      <div
        v-if="collection.goal"
        class="flex items-center"
        :style="{ gap: '8px' }"
      >
        <div
          class="flex-shrink-0 rounded-full"
          :style="{ backgroundColor: '#71C115', height: '4px', width: '40px' }"
        ></div>
        <BlTextBody2 text="Goal" :style="{ fontSize: '12px' }" />
      </div>
      <div
        v-if="collection.hasPhaseLine"
        class="flex items-center"
        :style="{ gap: '4px' }"
      >
        <div
          class="flex-shrink-0 rounded-full"
          :style="{
            background:
              'repeating-linear-gradient(90deg, #E4E4E4 0 6px, white 0 10px)',
            height: '4px',
            width: '40px'
          }"
        ></div>
        <BlTextBody2 text="Phase line" :style="{ fontSize: '12px' }" />
      </div>
      <div
        v-for="(legend, idx) in filterUniqueLegends(legends)"
        :key="`${legend.label}-${idx}`"
        class="flex items-center"
        :style="{ gap: '8px' }"
        :title="legend.label"
      >
        <div
          class="flex-shrink-0 rounded-full"
          :style="{
            backgroundColor: legend.color,
            height: '16px',
            width: '16px'
          }"
        ></div>
        <BlTextBody2
          :text="legend.label"
          class="truncate capitalize-first"
          :style="{ fontSize: '12px' }"
        />
      </div>
    </div>

    <div
      class="graph-menu"
      :class="{ 'flex-col': section.kind === 'graph_prompting' }"
    >
      <div
        v-if="
          section.kind === 'graph_prompting' &&
            defaultPrompts.length &&
            (promptingProgressBy === 'frequency' ||
              promptingProgressBy === 'percentage')
        "
        id="prompting-selection-show"
        class="mb-2"
      >
        <div
          class="flex items-center flex-shrink-0 w-full gap-3 pb-2 overflow-x-auto scrollbar-lg"
        >
          <div
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              isPromptingShowAll
                ? 'border-light-purple-300'
                : 'border-slate-400'
            ]"
            @click="onTogglePromptingShow('all')"
          >
            <BlIcon icon="prompt-line" class="flex-shrink-0 h-4" />
            <BlTextBody2 text="All graphs" class="capitalize-first" />
          </div>
          <div
            v-for="prompt in defaultPrompts"
            :key="prompt.id"
            class="w-30 py-2.5 px-3 flex flex-col items-center justify-center gap-1 flex-shrink-0 text-center border rounded cursor-pointer"
            :class="[
              prompt.is_show ? 'border-light-purple-300' : 'border-grey-200'
            ]"
            :title="prompt.name"
            @click="onTogglePromptingShow(prompt)"
          >
            <BlIcon
              v-if="prompt.shape && prompt.color"
              class="flex-shrink-0 w-auto h-4"
              :icon="'prompt-' + prompt.shape"
              :color="prompt.color"
            />
            <BlTextBody2
              :text="prompt.name"
              class="w-24 text-center truncate capitalize-first"
            />
          </div>
        </div>
      </div>
      <div class="flex items-center justify-center gap-3">
        <div class="w-6 h-6 cursor-pointer" @click="onClose">
          <BlIcon icon="close" class="w-6 h-6" clickable />
        </div>
        <div
          v-if="
            section.kind === 'graph_sbt' &&
              defaultTasks.length &&
              defaultProblemBehaviors.length
          "
          class="flex items-center gap-3"
        >
          <div class="relative">
            <div
              class="relative flex items-center justify-between h-8 px-4 border rounded cursor-pointer"
              :class="isTaskShowAll ? 'w-32' : 'w-42'"
              @click.prevent="showTasksOpened = !showTasksOpened"
            >
              <BlTextBody2
                :text="
                  isTaskShowAll
                    ? 'All tasks'
                    : `${
                        defaultTasks.filter(task => task.is_show).length
                      } task(s)`
                "
                class="capitalize-first"
              />
              <BlIcon
                class="absolute w-5 h-5 transition-all duration-300 right-4"
                icon="chevron-down"
                :style="showTasksOpened ? 'rotate: 180deg' : ''"
              />
            </div>
            <div
              v-if="showTasksOpened"
              class="absolute z-10 px-4 py-4 mt-5 space-y-4 overflow-y-auto border rounded shadow-md max-h-48 bg-pure-white border-grey-200"
              :class="isTaskShowAll ? '-right-12 w-56' : '-right-6 w-60'"
            >
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="onToggleTaskShow('all')"
              >
                <BlInputCheck
                  class="flex-shrink-0 pointer-events-none"
                  :checked="isTaskShowed('all')"
                />
                <BlTextBody2 text="All tasks" />
              </div>
              <div
                v-for="task in defaultTasks"
                :key="task.id"
                class="flex items-center gap-2 cursor-pointer"
                @click="onToggleTaskShow(task)"
              >
                <BlInputCheck
                  class="flex-shrink-0 pointer-events-none"
                  :checked="isTaskShowed(task)"
                />
                <BlTextBody2 :text="task.code" />
              </div>
            </div>
          </div>
          <div class="relative">
            <div
              class="relative flex items-center justify-between h-8 px-4 border rounded cursor-pointer"
              :class="isProblemBehaviorShowAll ? 'w-55' : 'w-64'"
              @click.prevent="
                showProblemBehaviorsOpened = !showProblemBehaviorsOpened
              "
            >
              <BlTextBody2
                :text="
                  isProblemBehaviorShowAll
                    ? 'All problem behaviors'
                    : `${
                        defaultProblemBehaviors.filter(pb => pb.is_show).length
                      } problem behavior(s)`
                "
                class="capitalize-first"
              />
              <BlIcon
                class="absolute w-5 h-5 transition-all duration-300 right-4"
                icon="chevron-down"
                :style="showProblemBehaviorsOpened ? 'rotate: 180deg' : ''"
              />
            </div>
            <div
              v-if="showProblemBehaviorsOpened"
              class="absolute z-10 w-64 px-4 py-4 mt-5 space-y-4 overflow-y-auto border rounded shadow-md max-h-48 bg-pure-white border-grey-200"
              :class="isProblemBehaviorShowAll ? '-right-22' : '-right-14'"
            >
              <div
                class="flex items-center gap-2 cursor-pointer"
                @click="onToggleProblemBehaviorShow('all')"
              >
                <BlInputCheck
                  class="flex-shrink-0 pointer-events-none"
                  :checked="isProblemBehaviorShowed('all')"
                />
                <BlTextBody2 text="All problem behaviors" />
              </div>
              <div
                v-for="pb in defaultProblemBehaviors"
                :key="pb.id"
                class="flex items-center gap-2 cursor-pointer"
                @click="onToggleProblemBehaviorShow(pb)"
              >
                <BlInputCheck
                  class="flex-shrink-0 pointer-events-none"
                  :checked="isProblemBehaviorShowed(pb)"
                />
                <BlTextBody2 :text="pb.code" />
              </div>
            </div>
          </div>
          <div
            class="bg-slate-300 h-7.5 flex-shrink-0"
            style="width: 2px;"
          ></div>
        </div>
        <div
          v-if="!useDateInputs"
          class="flex items-center justify-center gap-2 px-2 py-1 rounded w-140 bg-tulip-200"
        >
          <BlIcon icon="warning" color="tulip-7" class="w-5 h-5" />
          <div>
            <BlTextBody2
              text="This graph can't be modified because it was imported before the report's latest version."
              class="text-tulip-800"
            />
            <BlTextBody2
              text="Please delete it and add a new graph to make adjustments."
              class="text-tulip-800"
            />
          </div>
        </div>
        <div
          v-if="!useDateInputs"
          class="h-7.5 w-0.5 bg-slate-300 flex-shrink-0"
        ></div>
        <div v-if="usePromptingProgressBy" class="w-64">
          <BlSelectBox
            :key="
              `select-graph-progress-by-${JSON.stringify(
                promptingProgressByOptions
              )}`
            "
            prefixText="Progress by"
            v-model="promptingProgressBy"
            :options="promptingProgressByOptions"
            dense
            @change="onChangePromptingProgressBy($event)"
          />
        </div>
        <div
          v-if="usePromptingProgressBy"
          class="h-7.5 w-0.5 bg-slate-300 flex-shrink-0"
        ></div>
        <div v-if="useGraphType" class="w-40">
          <BlSelectBox
            v-model="graphType"
            :options="graphTypeOptions"
            dense
            @change="onChangeGraphType($event)"
          />
        </div>
        <div
          v-if="useGraphType"
          class="h-7.5 w-0.5 bg-slate-300 flex-shrink-0"
        ></div>
        <div v-if="useDateInputs" class="flex items-center gap-2">
          <BlInputDate
            :input="startDateInput"
            dense
            disabledManualInput
            @changed="onChangeDate"
          />
          <div class="h-0.5 w-4 bg-slate-500 flex-shrink-0"></div>
          <BlInputDate
            :input="endDateInput"
            dense
            disabledManualInput
            @changed="onChangeDate"
          />
        </div>
        <div
          v-if="useDateInputs"
          class="h-7.5 w-0.5 bg-slate-300 flex-shrink-0"
        ></div>
        <BlInputButton
          type="outlined-error"
          dense
          label="Delete graph"
          :loading="removeLoading"
          @clicked="onRemoveSection"
        />
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
import { displayDate } from "@/plugins/helper.js";
import {
  PROMPT_COLORS,
  PROMPT_POINTS,
  PHASE_LINE_COLORS
} from "@/plugins/assets.js";
import moment from "moment";

import LineChart from "../../charts/LineChart";
import BarChart from "../../charts/BarChart.js";

import BlTextSubtitle3 from "../../BlTextSubtitle3.vue";
import BlTextBody2 from "../../BlTextBody2.vue";
import BlIcon from "../../BlIcon.vue";
import BlInputDate from "../../BlInputDate.vue";
import BlInputButton from "../../BlInputButton.vue";
import BlSelectBox from "../../BlSelectBox.vue";
import MixedChart from "../../charts/MixedChart.vue";
import BlInputCheck from "../../BlInputCheck.vue";

export default {
  name: "ReportGraph",
  components: {
    LineChart,
    BarChart,
    BlTextSubtitle3,
    BlTextBody2,
    BlIcon,
    BlInputDate,
    BlInputButton,
    BlSelectBox,
    MixedChart,
    BlInputCheck
  },
  props: {
    section: { type: Object },
    mode: { type: String }, // edit | preview
    isFocused: { type: Boolean, default: false },
    isDragging: { type: Boolean, default: false }
    // @change-saving
    // @change-focus-id
  },
  data() {
    const defaultTarget = {
      id: 0,
      name: "",
      type: "",
      goal: "",
      date_mastered: "",
      prompting_format: "classic"
    };
    return {
      showDelay: false,
      //
      collection: {
        title: "",
        targetId: 0,
        goal: null,
        hasPhaseLine: false,
        prompts: [],
        tasks: [],
        problemBehaviors: [],
        labels: [],
        datasets: [],
        recordedBy: [],
        startDate: "",
        endDate: "",
        masteredDate: ""
      },
      startDateInput: { model: "" },
      endDateInput: { model: "" },
      legends: [],
      multiArbitraryLine: {
        id: "multiArbitraryLine",
        beforeDatasetsDraw(chart, args, options) {
          const {
            ctx,
            chartArea: { top, bottom },
            scales
          } = chart;
          const x = scales["x-axis-0"];
          const { indexes = [] } = options;
          ctx.save();

          function drawLines(indexPosition) {
            // const sideRight = x.getPixelForValue(null, indexPosition);
            // const sideLeft = x.getPixelForValue(null, indexPosition - 1);
            // const center = (sideRight - sideLeft) / 2 + sideLeft;
            const xPosition = x.getPixelForValue(null, indexPosition);
            //
            ctx.strokeStyle = "#E4E4E4";
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.setLineDash([10, 10]);
            ctx.moveTo(xPosition, top);
            ctx.lineTo(xPosition, bottom);
            ctx.stroke();
            ctx.closePath();
            ctx.restore();
            ctx.setLineDash([]);
          }

          for (let index = 0; index < indexes.length; index++) {
            drawLines(indexes[index]);
          }
        }
      },
      //
      graphPreviewLoading: false,
      defaultTarget,
      target: defaultTarget,
      defaultPrompts: [],
      defaultTasks: [],
      defaultProblemBehaviors: [],
      graphType: "line",
      graphTypeOptions: [
        { label: "Line", value: "line" },
        { label: "Stacked", value: "stacked" }
      ],
      promptingProgressBy: "frequency", // frequency | percentage | final_score
      //
      removeLoading: false,
      showTasksOpened: false,
      showProblemBehaviorsOpened: false
    };
  },
  computed: {
    ...mapGetters(["report"]),
    promptingProgressByOptions() {
      const arr = [
        { value: "frequency", label: "By frequency" },
        { value: "percentage", label: "By percentage" }
      ];
      if (this.target.prompting_format === "custom") {
        arr.unshift({
          value: "final_score",
          label: "By final score"
        });
      }
      return arr;
    },
    usePromptingProgressBy() {
      if (!this.section) return false;
      if (!this.section.kind) return false;
      if (!this.collection.targetId) return false;
      //
      if (this.section.kind === "graph_prompting") return true;
      return false;
    },
    useGraphType() {
      if (!this.section) return false;
      if (!this.section.kind) return false;
      //
      if (this.section.kind.includes("cumulative")) return true;
      return false;
    },
    useDateInputs() {
      if (!this.section) return false;
      if (!this.section.kind) return false;
      //
      if (this.section.kind.includes("cumulative")) return true;
      if (this.collection.targetId) return true;
      return false;
    },
    isPromptingShowAll() {
      let isAll = true;
      this.defaultPrompts.forEach(i => {
        if (!i.is_show) isAll = false;
      });
      return isAll;
    },
    isTaskShowAll() {
      let isAll = true;
      this.defaultTasks.forEach(i => {
        if (!i.is_show) isAll = false;
      });
      return isAll;
    },
    isProblemBehaviorShowAll() {
      let isAll = true;
      this.defaultProblemBehaviors.forEach(i => {
        if (!i.is_show) isAll = false;
      });
      return isAll;
    },
    isEmptyGraph() {
      if (this.mode !== "edit") return false;
      if (!this.useDateInputs) return false;
      if (this.collection.datasets.length) return false;
      return true;
    },
    lineChartOptions() {
      const { labels = [], recordedBy = [] } = this.collection;
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) phaseLineIndexes.push(idx);
      });
      //
      const options = {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
          usePointStyle: true,
          callbacks: {
            afterTitle: function(context) {
              if (recordedBy.at(context[0].index)) {
                return `Recorded by: ${recordedBy.at(context[0].index)}`;
              }
            }
          }
        },
        scales: {
          yAxes: [
            {
              ticks: {
                beginAtZero: true,
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: { display: phaseLineIndexes.length === 0 }
            }
          ],
          xAxes: [
            {
              scaleLabel: {
                display: true,
                labelString: "Date"
              },
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: { display: phaseLineIndexes.length === 0 }
            }
          ]
        },
        plugins: {
          multiArbitraryLine: {
            indexes: phaseLineIndexes
          }
        }
      };
      if (this.section.kind === "graph_duration") {
        options.scales.yAxes[0].ticks = {
          beginAtZero: true,
          fontColor: "#707070",
          fontSize: 12,
          callback: this.formatTime
        };
        options.tooltips = {
          usePointStyle: true,
          callbacks: {
            title: a => a[0].xLabel.join("/"),
            label: a => this.formatTime(Number(a.value || 0)),
            afterTitle: function(context) {
              if (recordedBy.at(context[0].index)) {
                return `Recorded by: ${recordedBy.at(context[0].index)}`;
              }
            }
          }
        };
      }
      if (
        this.section.kind === "graph_percentage" ||
        this.section.kind === "graph_pir" ||
        (this.section.kind === "graph_prompting" &&
          (this.promptingProgressBy === "percentage" ||
            this.promptingProgressBy === "final_score"))
      ) {
        options.scales.yAxes[0].ticks = {
          beginAtZero: true,
          fontColor: "#707070",
          fontSize: 12,
          max: 100
        };
      }
      if (this.section.kind === "graph_prompting") {
        const progressBy = {
          percentage: "Percentage (%)",
          frequency: "Frequency",
          final_score: "Final score (%)"
        };
        options.scales.yAxes[0] = {
          ...options.scales.yAxes[0],
          scaleLabel: {
            display: true,
            labelString: progressBy[this.promptingProgressBy]
          }
        };
      }
      if (this.section.kind === "graph") {
        options.scales.yAxes[0] = {
          ...options.scales.yAxes[0],
          scaleLabel: {
            display: true,
            labelString: "Frequency"
          }
        };
      }
      if (this.section.kind === "graph_pir") {
        options.scales.yAxes[0] = {
          ...options.scales.yAxes[0],
          scaleLabel: {
            display: true,
            labelString: "Percentage (%)"
          }
        };
      }
      if (this.section.kind === "graph_duration") {
        options.scales.yAxes[0] = {
          ...options.scales.yAxes[0],
          scaleLabel: {
            display: true,
            labelString: "Duration"
          }
        };
      }
      if (
        this.section.kind === "graph_percentage" ||
        this.section.kind === "graph_sbt"
      ) {
        options.scales.yAxes[0] = {
          ...options.scales.yAxes[0],
          scaleLabel: {
            display: true,
            labelString: "Score (%)"
          }
        };
      }
      return options;
    },
    stackedChartOptions() {
      const n = this.collection.labels ? this.collection.labels.length : 0;
      const barPercentage = n > 23 ? 0.9 : n / 25;
      return {
        legend: { display: false },
        maintainAspectRatio: false,
        tooltips: {
          callbacks: {
            title: (tooltipItem, data) => {
              const datasetsIndex = tooltipItem.at(0).datasetIndex;
              const dataIndex = tooltipItem.at(0).index;
              if (datasetsIndex < 0 || dataIndex < 0) {
                return null;
              }
              const curriculumLabel = data.datasets.at(datasetsIndex).label;
              const valueStacked = data.datasets
                .at(datasetsIndex)
                .data.at(dataIndex);
              return `${curriculumLabel}: ${valueStacked}`;
            },
            label: a => a.xLabel.join(" "),
            footer: (tooltipItem, data) => {
              const dataIndex = tooltipItem.at(0).index;
              if (dataIndex < 0) {
                return null;
              }
              let totalStacked = 0;
              for (let i = 0; i < data.datasets.length; i++) {
                totalStacked += data.datasets.at(i).data.at(dataIndex);
              }
              return `Total: ${totalStacked} mastered target(s)`;
            }
          }
        },
        scales: {
          yAxes: [
            {
              stacked: true,
              ticks: {
                beginAtZero: true,
                fontColor: "#707070",
                fontSize: 12
              },
              gridLines: { display: true }
            }
          ],
          xAxes: [
            {
              stacked: true,
              ticks: {
                fontColor: "#707070",
                fontSize: 12
              },
              barPercentage,
              gridLines: { display: true }
            }
          ]
        }
      };
    }
  },
  watch: {
    isDragging(val) {
      if (val) {
        this.showDelay = true;
        setTimeout(() => {
          this.showDelay = false;
        }, 1500);
      }
    }
  },
  mounted() {
    this.initiateCollectionFromSection(this.section);
  },
  methods: {
    ...mapActions([
      "actionGet",
      "getGraphDataSection",
      "updateSection",
      "deleteSection"
    ]),
    displayDate,
    onClose() {
      setTimeout(() => {
        this.$emit("change-focus-id", 0);
      }, 10);
    },
    initiateCollectionFromSection(val) {
      if (!val) return;
      if (!val.data) return;

      const json = JSON.parse(val.data);
      const {
        title = "",
        targetId = 0,
        goal = null,
        hasPhaseLine = false,
        prompts = [],
        tasks = [],
        problemBehaviors = [],
        labels = [],
        datasets = [],
        recordedBy = [],
        startDate = "",
        endDate = "",
        masteredDate = "",
        promptingProgressBy = "frequency"
      } = json;

      /**
       kind
       * graph -> frequency / other
       * graph_percentage -> percentage
       * graph_pir -> pir
       * graph_duration -> duration
       * graph_prompting -> prompting
       * graph_sbt -> sbt
       * cumulative_graph_line
       * cumulative_graph_stacked
      */
      if (val.kind === "graph_prompting") {
        this.promptingProgressBy = promptingProgressBy;
      }
      if (val.kind === "cumulative_graph_line") {
        this.graphType = "line";
      }
      if (val.kind === "cumulative_graph_stacked") {
        this.graphType = "stacked";
      }

      //
      if (datasets.length) {
        this.legends = datasets
          .map(i => {
            if (i.label === "Goal" || !i.label) return null;
            const defaultColor = "#9A8AC2";
            const bg = i.backgroundColor || defaultColor;
            const border = i.borderColor || defaultColor;
            // check color with old version
            const color = bg.includes("rgba") ? border : bg;
            return {
              label: i.label,
              color,
              type: i.taskId ? "task" : i.behaviorId ? "problemBehavior" : ""
            };
          })
          .filter(i => i);
        if (this.section.kind === "cumulative_graph_stacked") {
          this.legends = this.legends.sort((a, b) => {
            if (a.label < b.label) return -1;
            if (a.label > b.label) return 1;
            return 0;
          });
        }
      }
      //
      // this.target.id = targetId;
      this.startDateInput.model = startDate;
      this.endDateInput.model = endDate;

      //
      this.collection = {
        title,
        targetId,
        goal,
        hasPhaseLine,
        prompts,
        tasks,
        problemBehaviors,
        labels,
        datasets,
        recordedBy,
        startDate,
        endDate,
        masteredDate
      };

      if (val.kind === "graph_prompting") {
        this.defaultPrompts = prompts.map(i => {
          return { ...i, is_show: i.is_show === false ? i.is_show : true };
        });
      }
      if (val.kind === "graph_sbt") {
        this.defaultTasks = tasks.map(i => {
          return { ...i, is_show: i.is_show === false ? i.is_show : true };
        });
        this.defaultProblemBehaviors = problemBehaviors.map(i => {
          return { ...i, is_show: i.is_show === false ? i.is_show : true };
        });
      }
      if (targetId && targetId !== "0") {
        this.fetchGraphDataSection();
      }
    },
    //
    onChangeGraphType(val) {
      if (this.graphType === val) return;
      this.graphType = val;
      this.fetchGraphDataSection();
    },
    onChangePromptingProgressBy(val) {
      if (this.promptingProgressBy === val) return;
      this.promptingProgressBy = val;
      this.fetchGraphDataSection();
    },
    onChangeDate(payload) {
      if (payload.input.model === payload.value) return;
      payload.input.model = payload.value;
      this.fetchGraphDataSection();
    },
    //
    async fetchGraphDataSection() {
      if (!this.section) return;
      // if (this.mode !== "edit") return;

      this.graphPreviewLoading = true;
      this.$emit("change-saving", true);

      const type = this.section.kind.includes("cumulative")
        ? "cumulative"
        : "target";
      const id =
        type === "cumulative"
          ? Number(this.report.client_id)
          : Number(this.collection.targetId);

      const targetAndNotSameId = type === "target" && id !== this.target.id;
      const targetAndPrompting =
        type === "target" && this.section.kind === "graph_prompting";
      const targetAndSbt =
        type === "target" && this.section.kind === "graph_sbt";

      if (targetAndNotSameId) {
        const params = { url: `/api/v1/targets/${id}` };
        const { data } = await this.actionGet(params);
        this.target = data || this.defaultTarget;

        if (targetAndPrompting) {
          // generate default prompts
          if (data.type === "Target::Prompting") {
            if (!this.defaultPrompts.length) {
              this.defaultPrompts = data.prompts.map(i => {
                return { ...i, is_show: true };
              });
            }
          }
        }

        if (targetAndSbt) {
          // generate default tasks
          if (data.type === "Target::Sbt") {
            if (!this.defaultTasks.length) {
              this.defaultTasks = data.target_tasks.map(i => {
                return { ...i, is_show: true };
              });
            }
            if (!this.defaultProblemBehaviors.length) {
              this.defaultProblemBehaviors = data.target_problem_behaviors.map(
                i => {
                  return { ...i, is_show: true };
                }
              );
            }
          }
        }
      }

      if (type === "cumulative") {
        this.target = this.defaultTarget;
      }

      let params = `?`;
      if (this.startDateInput.model) {
        params += `start_date=${this.startDateInput.model}&`;
      }
      if (this.endDateInput.model) {
        params += `end_date=${this.endDateInput.model}&`;
      }

      if (type === "cumulative") {
        params += `view=graph&group_by=month&`;
        params += `graph_type=${this.graphType}&`;
      }

      if (this.section.kind === "graph_prompting") {
        params += `progress_by=${this.promptingProgressBy}&`;
      }

      // remove last char `&`
      params = params.slice(0, -1);

      let graphData = {
        labels: this.collection.labels,
        datasets: this.collection.datasets,
        recorded_by: this.collection.recordedBy
      };

      const res = await this.getGraphDataSection({ id, type, params });
      if (res) graphData = res;

      const graph = this.generateCollection(graphData);
      this.collection = graph;
      this.onUpdateGraph(graph);
    },
    generateCollection(val) {
      const { labels = [], datasets = [], recorded_by: recordedBy = [] } = val;

      const prompts = this.defaultPrompts;
      const tasks = this.defaultTasks;
      const problemBehaviors = this.defaultProblemBehaviors;
      const isCumulative = this.section.kind.includes("cumulative");
      const isStacked = this.graphType === "stacked";
      const isPrompting = this.target.type === "Target::Prompting";
      const isSbt = this.target.type === "Target::Sbt";
      const progressBy = isPrompting ? this.promptingProgressBy : "";

      // setup goal number
      let newGoal = null;
      if (this.target.goal) {
        newGoal = this.target.goal;
        if (
          progressBy === "percentage" ||
          (progressBy === "frequency" &&
            this.target.prompting_format === "custom")
        ) {
          newGoal = null;
        }
      }

      // setup prompts
      let newPrompts = [];
      let newTasks = [];
      let newProblemBehaviors = [];
      if (isPrompting) {
        newPrompts = prompts.map(i => {
          return {
            id: i.id,
            name: i.name,
            abbreviation: i.abbreviation,
            color: i.color,
            shape: i.shape,
            position: i.position,
            is_show: i.is_show
          };
        });
      }
      if (isSbt) {
        newTasks = tasks.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: i.is_show
          };
        });
        newProblemBehaviors = problemBehaviors.map(i => {
          return {
            id: i.id,
            code: i.code,
            code_definition: i.code_definition,
            color: i.color,
            is_show: i.is_show
          };
        });
      }

      // setup labels and hasPhaseLine value
      let newLabels = [];
      const phaseLineIndexes = [];
      labels.forEach((i, idx) => {
        if (!i) {
          newLabels[idx] = "";
          phaseLineIndexes.push(idx);
        } else {
          const display = isCumulative ? "month" : "date";
          let res = "";
          if (display === "date") {
            const arr = i.split("-").reverse();
            res = [`${arr.at(0)}/${arr.at(1)}`, arr.at(2)];
          }
          if (display === "month") {
            res = [moment(i).format("MMM"), moment(i).format("YYYY")];
          }
          newLabels[idx] = res;
        }
      });

      let newDatasets = [];
      if (datasets.length) {
        if (isCumulative && isStacked) {
          // setup core datasets property [stacked]
          datasets.forEach((i, idx) => {
            const bg =
              i.color +
              Math.round(Math.min(Math.max(0.75), 1) * 255)
                .toString(16)
                .toUpperCase();
            newDatasets[idx] = {
              label: i.label,
              data: i.data,
              backgroundColor: bg,
              borderColor: "#E4E4E4",
              hoverBorderColor: "#9A8AC2",
              borderWidth: 0,
              hoverBorderWidth: 2,
              borderRadius: 15
            };
          });
        } else {
          // setup core datasets property [line]
          if (isPrompting && progressBy !== "final_score") {
            // promting with frequency and percentage progress_by
            // prepare prompting property
            newDatasets = newPrompts.map(i => {
              return {
                promptId: i.id,
                label: i.name,
                data: [],
                // line style
                backgroundColor: PROMPT_COLORS[i.color].primaryColor,
                borderColor: PROMPT_COLORS[i.color].primaryColor,
                // point style
                pointStyle: PROMPT_POINTS[i.shape].pointStyle,
                pointBackgroundColor: PROMPT_COLORS[i.color].primaryColor,
                pointBorderColor: PROMPT_COLORS[i.color].secondaryColor,
                pointRadius: 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
            // input prompting data value
            datasets[0].data.forEach(entry => {
              newPrompts.forEach((prompt, idx) => {
                let val = null;
                if (prompt.is_show && entry) {
                  val = entry[prompt.id];
                }
                newDatasets[idx].data.push(val);
              });
            });
            // add goal for prompting with frequency present
            if (
              progressBy === "frequency" &&
              this.target.prompting_format === "classic"
            ) {
              const goalDataset = {
                label: datasets[1].label,
                data: datasets[1].data,
                backgroundColor: "#71C115",
                borderColor: "#71C115",
                pointRadius: 0,
                fill: false,
                tension: 0.1
              };
              newDatasets.push(goalDataset);
            }
          } else {
            // includes prompting with final_score progress_by
            newDatasets = datasets.map(i => {
              const color = i.color ? i.color : "primary"; // set 'primary' for default color
              const propertyColor =
                i.label === "Goal" ? "#71C115" : PHASE_LINE_COLORS[color];
              return {
                label: i.label,
                data: i.data,
                backgroundColor: propertyColor,
                borderColor: propertyColor,
                pointRadius: i.label === "Goal" ? 0 : 4,
                hoverRadius: 4,
                fill: false,
                tension: 0.1
              };
            });
          }
        }
      }

      if (
        datasets &&
        datasets.data &&
        datasets.data.tasks &&
        datasets.data.problem_behaviors
      ) {
        newDatasets = [
          ...newTasks.map(i => ({
            taskId: i.id,
            type: "line",
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            pointBackgroundColor: i.color,
            pointBorderColor: i.color,
            pointRadius: 4,
            hoverRadius: 4,
            fill: false,
            tension: 0.1
          })),
          ...newProblemBehaviors.map(i => ({
            type: "bar",
            behaviorId: i.id,
            label: i.code,
            data: [],
            backgroundColor: i.color,
            borderColor: i.color,
            yAxisID: "y-axis-bar",
            barPercentage: 10,
            barThickness: 100,
            maxBarThickness: 100,
            minBarLength: 10
          }))
        ];

        // Proses data untuk defaultTasks
        datasets.data.tasks.forEach(entry => {
          newTasks.forEach((task, idx) => {
            let val = null;
            if (task.is_show && entry && entry[task.id]) {
              val = entry[task.id].score;
            }
            newDatasets[idx].data.push(val);
          });
        });

        // Proses data untuk problem behaviors
        datasets.data.problem_behaviors.forEach(entry => {
          newProblemBehaviors.forEach((behavior, idx) => {
            let val = null;
            if (behavior.is_show && entry && entry[behavior.id]) {
              val = entry[behavior.id].score;
            }
            newDatasets[newTasks.length + idx].data.push(val);
          });
        });
      }

      // generate trendline and average line
      let isTargetHasTrendline = true;
      if (isCumulative) isTargetHasTrendline = false;
      if (isPrompting) {
        if (progressBy === "final_score") isTargetHasTrendline = true;
        else {
          const isShowPrompts = [...newPrompts].filter(i => i.is_show);
          if (isShowPrompts.length === 1) isTargetHasTrendline = true;
          else isTargetHasTrendline = false;
        }
      }
      if (isSbt) isTargetHasTrendline = false;

      let isTargetHasAverage = true;
      if (isCumulative) isTargetHasAverage = false;
      if (isPrompting || isSbt) isTargetHasAverage = false;
      if (phaseLineIndexes.length > 0) isTargetHasAverage = false;

      const trendlines = [];
      const averageLines = [];
      if (isTargetHasTrendline) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // trendline
            const arr = { a: [], b: [], c: [] };
            let firstAttemp = false;
            let endOfFirtsAttemp = false;
            dataset.data.forEach((val, index) => {
              /*
                    if val === null && index === 0  => first=true
                    if first
                      if      val === null && !endA => a.push
                      elseif  val !== null          => endA=true; b.push
                      elseif  val === null          => c.push
                    else
                      if      val !== null          => b.push
                      elseif  val === null          => c.push
                    end
                    */
              if (val === null && index === 0) {
                firstAttemp = true;
              }
              if (firstAttemp) {
                if (val === null && !endOfFirtsAttemp) {
                  arr.a.push(val);
                } else if (val !== null) {
                  endOfFirtsAttemp = true;
                  arr.b.push(val);
                } else if (val === null) arr.c.push(val);
              } else {
                if (val !== null) arr.b.push(val);
                else if (val === null) arr.c.push(val);
              }
            });
            // start generate trendline
            const idx = arr.b.length - 1;
            const dt = [
              arr.b[0],
              ...this.interpolate(arr.b[0], arr.b[idx], idx - 1),
              arr.b[idx]
            ];
            // remove the trendline if there are only 2 points
            if (dt.length === 2) {
              dt.pop();
            }
            let res = [...arr.a, ...dt, ...arr.c];
            const trendline = {
              key: "trendline",
              label: "Trendline",
              data: res,
              borderColor: "#6750A4",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 0
            };
            trendlines.push(trendline);
          }
        });
      }
      if (isTargetHasAverage) {
        newDatasets.forEach(dataset => {
          // only generate non-goal label
          if (dataset.label !== "Goal") {
            // average line
            let totalScore = 0;
            let totalCount = 0;
            dataset.data.forEach(val => {
              if (val !== null) {
                totalScore += val;
                totalCount += 1;
              }
            });
            // start generate average
            const average = Number((totalScore / totalCount).toFixed(0));
            const resAverage = Array(totalCount).fill(average);
            const averageLine = {
              key: "average",
              label: "Average",
              data: resAverage,
              borderColor: "#71C115",
              backgroundColor: "rgba(0, 0, 0, 0)",
              borderWidth: 2,
              borderDash: [12],
              pointRadius: 2
            };
            averageLines.push(averageLine);
          }
        });
      }
      trendlines.forEach(i => {
        newDatasets.push(i);
      });
      averageLines.forEach(i => {
        newDatasets.push(i);
      });

      // generate legends
      if (newDatasets.length) {
        this.legends = newDatasets
          .map(i => {
            if (!i.label) return null;
            if (i.label === "Goal") return null;
            if (i.key) {
              if (i.key === "trendline") return null;
              if (i.key === "average") return null;
            }
            return {
              label: i.label,
              color: i.backgroundColor
            };
          })
          .filter(i => i);
        if (isCumulative) {
          this.legends = this.legends.sort((a, b) => {
            if (a.label < b.label) return -1;
            if (a.label > b.label) return 1;
            return 0;
          });
        }
      } else this.legends = [];

      let newMasteredDate = "";
      if (this.target.date_mastered) {
        newMasteredDate = this.target.date_mastered;
      }

      return {
        title: this.collection.title,
        targetId: this.collection.targetId,
        goal: newGoal,
        hasPhaseLine: phaseLineIndexes.length > 0,
        prompts: newPrompts,
        tasks: newTasks,
        problemBehaviors: newProblemBehaviors,
        labels: newLabels,
        datasets: newDatasets,
        recordedBy: recordedBy,
        startDate: this.startDateInput.model,
        endDate: this.endDateInput.model,
        masteredDate: newMasteredDate,
        promptingProgressBy: progressBy
      };
    },
    interpolate(first, last, count) {
      const interpolatedPoints = [];

      // Calculate the difference between start and end coordinates
      const deltaX = (last - first) / (count + 1);

      // Interpolate the points
      for (let i = 1; i <= count; i++) {
        const interpolatedX = Number(first + deltaX * i);
        interpolatedPoints.push(interpolatedX);
      }

      return interpolatedPoints;
    },
    onTogglePromptingShow(prompt = "all") {
      if (prompt === "all") {
        const result = !this.isPromptingShowAll;
        this.defaultPrompts = this.defaultPrompts.map(i => ({
          ...i,
          is_show: result
        }));
      } else {
        const idx = this.defaultPrompts.findIndex(i => i.id === prompt.id);
        if (idx > -1) {
          this.defaultPrompts[idx].is_show = !this.defaultPrompts[idx].is_show;
        }
      }
      //
      this.fetchGraphDataSection();
    },

    onToggleTaskShow(task = "all") {
      if (task === "all") {
        const result = !this.isTaskShowAll;
        this.defaultTasks = this.defaultTasks.map(i => ({
          ...i,
          is_show: result
        }));
      } else {
        const idx = this.defaultTasks.findIndex(i => i.id === task.id);
        if (idx > -1) {
          this.defaultTasks[idx].is_show = !this.defaultTasks[idx].is_show;
        }
      }
      //
      this.fetchGraphDataSection();
    },

    isTaskShowed(taskOption = "all") {
      if (taskOption === "all") return this.isTaskShowAll;
      const idx = this.defaultTasks.findIndex(i => i.id === taskOption.id);
      if (idx > -1) {
        return this.defaultTasks[idx].is_show;
      }
      return false;
    },

    onToggleProblemBehaviorShow(problemBehavior = "all") {
      if (problemBehavior === "all") {
        const result = !this.isProblemBehaviorShowAll;
        this.defaultProblemBehaviors = this.defaultProblemBehaviors.map(i => ({
          ...i,
          is_show: result
        }));
      } else {
        const idx = this.defaultProblemBehaviors.findIndex(
          i => i.id === problemBehavior.id
        );
        if (idx > -1) {
          this.defaultProblemBehaviors[idx].is_show = !this
            .defaultProblemBehaviors[idx].is_show;
        }
      }
      //
      this.fetchGraphDataSection();
    },

    isProblemBehaviorShowed(problemBehaviorOption = "all") {
      if (problemBehaviorOption === "all") return this.isProblemBehaviorShowAll;
      const idx = this.defaultProblemBehaviors.findIndex(
        i => i.id === problemBehaviorOption.id
      );
      if (idx > -1) {
        return this.defaultProblemBehaviors[idx].is_show;
      }
      return false;
    },
    async onUpdateGraph(graph) {
      const stringify = JSON.stringify(graph);
      let kind = this.section.kind;
      /**
       kind
       * graph -> frequency / other
       * graph_percentage -> percentage
       * graph_pir -> pir
       * graph_duration -> duration
       * graph_prompting -> prompting
       * graph_sbt -> sbt
       * cumulative_graph_line
       * cumulative_graph_stacked
       */

      const targetId = this.collection.targetId;
      if (kind === "graph" && targetId) {
        const params = { url: `/api/v1/targets/${targetId}` };
        const { data } = await this.actionGet(params);
        this.target = data || this.defaultTarget;
        if (data.type === "Target::Percentage") {
          kind = "graph_percentage";
        }
        if (data.type === "Target::Pir") {
          kind = "graph_pir";
        }
      }

      const isCumulative = kind.includes("cumulative");
      const isStacked = this.graphType === "stacked";
      if (isCumulative) {
        kind = isStacked ? "cumulative_graph_stacked" : "cumulative_graph_line";
      }
      const data = {
        id: this.section.id,
        section: { data: stringify, kind }
      };
      await this.updateSection(data);
      this.graphPreviewLoading = false;
      this.$emit("change-saving", false);
    },
    //
    filterUniqueLegends(arr) {
      const uniqueSet = new Set();
      const filteredArray = arr.filter(obj => {
        const uniqueKey = obj.label + obj.color + obj.name + obj.shape;
        if (!uniqueSet.has(uniqueKey)) {
          uniqueSet.add(uniqueKey);
          return true;
        }
        return false;
      });
      return filteredArray;
    },
    formatTime(x) {
      let hours = "00";
      let minutes = "00";
      let seconds = "00";
      if (typeof x === "number") {
        seconds = (x % 60).toString().padStart(2, "0");
        if (x >= 60) {
          minutes = Math.floor(x / 60);
          if (x >= 3600) minutes = minutes % 60;
          minutes = minutes.toString().padStart(2, "0");
        }
        if (x >= 3600) {
          hours = Math.floor(x / 3600)
            .toString()
            .padStart(2, "0");
        }
      }
      return `${hours}:${minutes}:${seconds}`;
    },
    //
    async onRemoveSection() {
      if (this.mode !== "edit") return;
      this.removeLoading = true;
      await this.deleteSection({ id: this.section.id });
      this.removeLoading = false;
    }
  }
};
</script>

<style lang="scss">
.blum-report-graph {
  position: relative;

  .graph-menu {
    position: absolute;
    left: 50%;
    top: 100%;
    transform: translateX(-50%);
    z-index: 1;
    max-width: 64rem;
    margin-top: 1rem;
    padding: 0.75rem 1rem;
    display: none;
    border: 1px solid var(--Grey-2, #e4e4e4);
    border-radius: 5px;
    background-color: white;
  }
  &.section-focused {
    .graph-menu {
      display: flex;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/reports/editor/ReportGraph.vue